import Vue from "vue"
import { tracker } from "@openreplay/tracker"

import store from "./store"
import router from "./config/router/index"
import i18n from "./config/i18n"

import "@/config/auth"
import "@/config/http"
import "@/config/validation-rules"

import { ValidationProvider, ValidationObserver, localize, localeChanged } from "vee-validate"
import PermissionsPlugin from "@/plugins/permissions"
import CookieConsentPlugin from "@/plugins/cookieConsent"
import PortalVue from "portal-vue"

Vue.component("ValidationProvider", ValidationProvider)
Vue.component("ValidationObserver", ValidationObserver)

import App from "./App.vue"

Vue.use(PortalVue)

import {
    OverlayPlugin,
    SpinnerPlugin,
    LayoutPlugin,
    CollapsePlugin,
    DropdownPlugin,
    NavPlugin,
    NavbarPlugin,
    ModalPlugin,
    BreadcrumbPlugin,
    ButtonPlugin,
    FormPlugin,
    FormGroupPlugin,
    FormRadioPlugin,
    TooltipPlugin,
    TabsPlugin,
    AlertPlugin,
} from "bootstrap-vue"

Vue.use(LayoutPlugin)
Vue.use(OverlayPlugin)
Vue.use(SpinnerPlugin)
Vue.use(ModalPlugin)
Vue.use(BreadcrumbPlugin)
Vue.use(ButtonPlugin)
Vue.use(FormPlugin)
Vue.use(FormGroupPlugin)
Vue.use(FormRadioPlugin)
Vue.use(CollapsePlugin)
Vue.use(DropdownPlugin)
Vue.use(NavPlugin)
Vue.use(NavbarPlugin)
Vue.use(TooltipPlugin)
Vue.use(TabsPlugin)
Vue.use(AlertPlugin)

import FloatingVue from "floating-vue"

FloatingVue.options.themes.tooltip.delay.show = 100
Vue.use(FloatingVue)

import VueGoodTablePlugin from "vue-good-table"
import "vue-good-table/dist/vue-good-table.css"

Vue.use(VueGoodTablePlugin)

// Vue mask
import { vMaska } from "maska"

// @ts-ignore
Vue.directive("maska", vMaska)

// @ts-ignore
import vSelect from "vue-select"

Vue.component("v-select", vSelect)

import VueLoading from "vue-loading-overlay"

Vue.component("vue-loading", VueLoading)

// @ts-ignore
import VueInputAutowidth from "vue-input-autowidth"

Vue.use(VueInputAutowidth)

Vue.config.productionTip = false

import "leaflet/dist/leaflet.css"

import "./assets/scss/main.scss"

Vue.mixin({
    methods: {
        fromLocalString: function (str) {
            let multiply = 1
            const decimalSep = (1.1).toLocaleString(this.$i18n.locale).substring(1, 2)
            const thousandSep = (1000.1).toLocaleString(this.$i18n.locale).substring(1, 2)
            str = str.replace(thousandSep, "")
            str = str.replace(decimalSep, ".")

            if (str.indexOf("−") > -1) {
                str = str.substring(1)
                multiply = -1
            }

            return parseFloat(str) * multiply
        },
        isNumeric(value) {
            return /^-?\d+$/.test(value)
        },
    },
})

const debug = process.env.NODE_ENV !== "production"

if (debug) {
    Vue.config.devtools = true
}

Vue.use(PermissionsPlugin, {
    store,
})
Vue.use(CookieConsentPlugin)

// Set global variables
const brand = process.env.VUE_APP_BRAND || "default"

Vue.prototype.$brand = brand
Vue.prototype.$title = process.env.VUE_APP_TITLE || "Reduxi - Cloud"

// Brand specific assets
import(`./assets/brands/${brand}/scss/brand.scss`)

const locale = i18n.locale
import moment from "moment"

import en from "vee-validate/dist/locale/en.json"

import "moment/locale/de"
import de from "vee-validate/dist/locale/de.json"

import "moment/locale/sl"
import sl from "vee-validate/dist/locale/sl.json"

import "moment/locale/sk"
import sk from "vee-validate/dist/locale/sk.json"

import "moment/locale/sv"
import sv from "vee-validate/dist/locale/sv.json"

import "moment/locale/nl"
import nl from "vee-validate/dist/locale/nl.json"

import "moment/locale/it"
import it from "vee-validate/dist/locale/it.json"

import "moment/locale/hr"
import hr from "vee-validate/dist/locale/hr.json"

import "moment/locale/pl"
import pl from "vee-validate/dist/locale/pl.json"

import "moment/locale/pt"
import pt from "vee-validate/dist/locale/pt_PT.json"

import "moment/locale/sr"
import sr from "vee-validate/dist/locale/sr_Latin.json"

import "moment/locale/cs"
import cs from "vee-validate/dist/locale/cs.json"

import "moment/locale/bs"
import bs from "@/utils/vee-validate/bs.json"

import "moment/locale/mk"
import mk from "@/utils/vee-validate/bs.json"

import "moment/locale/ro"
import ro from "vee-validate/dist/locale/ro.json"

import "moment/locale/fr"
import fr from "vee-validate/dist/locale/fr.json"

import "moment/locale/es"
import es from "vee-validate/dist/locale/es.json"

import { fetchTranslations } from "./utils/i18n-utils"

localize({ bs, cs, de, en, hr, it, mk, nl, pl, pt, sk, sl, sr, sv, fr, ro, es })

moment.locale(locale)
localize(locale)
localeChanged()

async function initApp() {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        const identifier = urlParams.get("identifier")

        await store.dispatch("siteInfo/fetchSiteInfo", identifier)

        store.dispatch("siteInfo/checkMaintenanceNotification")

        if (!window.location.pathname.startsWith("/auth")) {
            await store.dispatch("auth/fetch")
        }

        const translations = await fetchTranslations(locale)

        if (translations) {
            i18n.mergeLocaleMessage(locale, translations)
        }

        const vm = new Vue({
            el: "#app",
            store: store,
            router: router,
            i18n: i18n,
            render: (h) => h(App),
        }).$mount("#app")

        if (process.env.VUE_APP_OPENREPLAY_PROJECT_KEY) {
            tracker.configure({
                projectKey: process.env.VUE_APP_OPENREPLAY_PROJECT_KEY,
                ingestPoint: process.env.VUE_APP_OPENREPLAY_INGEST_POINT,
                capturePerformance: false,
                captureResourceTimings: false,
                consoleMethods: ["error"],
                network: {
                    failuresOnly: true,
                    capturePayload: false,
                    captureInIframes: false,
                    sessionTokenHeader: false,
                    ignoreHeaders: true,
                },
            })

            const startTracker = () => {
                const user = store.getters["auth/user"]
                tracker.start({
                    userID: user?.email ?? undefined,
                })
            }

            const forceStart = process.env.VUE_APP_OPENREPLAY_FORCE_START === "true"

            if (forceStart || vm.$cookieConsent.hasConsentTo("analyticsCookies")) {
                startTracker()
            }

            if (!forceStart) {
                vm.$watch(
                    () => vm.$cookieConsent.hasConsentTo("analyticsCookies"),
                    (value: boolean) => {
                        if (value) {
                            startTracker()
                        } else {
                            tracker.stop()
                        }
                    },
                )
            }
        }
    } catch (error: any) {
        console.error("Error loading user data: ", error)
    }
}

if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
            registration.unregister()
        })
    })
}

initApp()
